// Guided-tour engine - spotlight + tooltip product tours for the storefront.
//
// Self-contained (no third-party library): an SVG overlay with an animated
// cut-out around the current element, a pulsing focus ring, and a branded card
// with progress, back/next, keyboard + screen-reader support. On narrow screens
// the card docks as a bottom sheet.
//
// A tour is { id, version, title, steps: [...] }. A step:
//   target      selector string | () => Element | null   (omit → centred card)
//   title, body body is trusted HTML authored in the tour config
//   icon        key of ICONS below
//   placement   'auto' | 'top' | 'bottom' | 'left' | 'right'
//   padding     px of breathing room around the target (default 8)
//   when()      return false to skip the step (e.g. guest vs signed-in)
//   before()    async - prepare the page (open a drawer, scroll a panel …)
//   optional    true → silently skip when the target never appears
//   waitFor     ms to wait for the target to render (default 6000)
//   interactive { on: 'change'|'input'|'click', target?, hint, done,
//                 validate?(el, evt), autoAdvance? (default true) }
//               → the user may click through the cut-out; the step waits for
//                 them to actually do the thing, then celebrates and moves on.
//
// Styles are injected once and fully scoped under .gwt-* so the tour ships with
// the app bundle and needs no ThemeSass re-save.

var STYLE_ID = 'gwt-styles';
var Z = 2147483600; // above AddVehicleModal (2147483000) and every storefront drawer/modal
var GAP = 14;

// Lucide icons (ISC licence, lucide.dev): the same set the Parts Finder SPA uses,
// so tour glyphs match the rest of the site. 24px grid, 2px stroke.
export var ICONS = {
  car: '<path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.4-1.1-.7-1.8-.7H5c-.6 0-1.1.4-1.4.9l-1.4 2.9A3.7 3.7 0 0 0 2 12v4c0 .6.4 1 1 1h2"/><circle cx="7" cy="17" r="2"/><path d="M9 17h6"/><circle cx="17" cy="17" r="2"/>',
  search: '<circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/>',
  filter: '<path d="M3 6h18"/><path d="M7 12h10"/><path d="M10 18h4"/>',
  sort: '<path d="m21 16-4 4-4-4"/><path d="M17 20V4"/><path d="m3 8 4-4 4 4"/><path d="M7 4v16"/>',
  pin: '<path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0"/><circle cx="12" cy="10" r="3"/>',
  locate: '<line x1="2" x2="5" y1="12" y2="12"/><line x1="19" x2="22" y1="12" y2="12"/><line x1="12" x2="12" y1="2" y2="5"/><line x1="12" x2="12" y1="19" y2="22"/><circle cx="12" cy="12" r="7"/><circle cx="12" cy="12" r="3"/>',
  store: '<path d="m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7"/><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4"/><path d="M2 7h20"/><path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7"/>',
  garage: '<path d="M22 8.35V20a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8.35A2 2 0 0 1 3.26 6.5l8-3.2a2 2 0 0 1 1.48 0l8 3.2A2 2 0 0 1 22 8.35Z"/><path d="M6 18h12"/><path d="M6 14h12"/><rect width="12" height="12" x="6" y="10"/>',
  returns: '<path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"/>',
  reset: '<path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8"/><path d="M3 3v5h5"/>',
  user: '<path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>',
  userplus: '<path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" x2="19" y1="8" y2="14"/><line x1="22" x2="16" y1="11" y2="11"/>',
  login: '<path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/><polyline points="10 17 15 12 10 7"/><line x1="15" x2="3" y1="12" y2="12"/>',
  mail: '<rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>',
  phone: '<rect width="14" height="20" x="5" y="2" rx="2" ry="2"/><path d="M12 18h.01"/>',
  lock: '<rect width="18" height="11" x="3" y="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>',
  key: '<path d="M2.586 17.414A2 2 0 0 0 2 18.828V21a1 1 0 0 0 1 1h3a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h1a1 1 0 0 0 1-1v-1a1 1 0 0 1 1-1h.172a2 2 0 0 0 1.414-.586l.814-.814a6.5 6.5 0 1 0-4-4z"/><circle cx="16.5" cy="7.5" r=".5" fill="currentColor"/>',
  cart: '<circle cx="8" cy="21" r="1"/><circle cx="19" cy="21" r="1"/><path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"/>',
  list: '<path d="M3 12h.01"/><path d="M3 18h.01"/><path d="M3 6h.01"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M8 6h13"/>',
  grid: '<rect width="7" height="7" x="3" y="3" rx="1"/><rect width="7" height="7" x="14" y="3" rx="1"/><rect width="7" height="7" x="14" y="14" rx="1"/><rect width="7" height="7" x="3" y="14" rx="1"/>',
  eye: '<path d="M2.062 12.348a1 1 0 0 1 0-.696 10.75 10.75 0 0 1 19.876 0 1 1 0 0 1 0 .696 10.75 10.75 0 0 1-19.876 0"/><circle cx="12" cy="12" r="3"/>',
  compass: '<path d="m16.24 7.76-1.804 5.411a2 2 0 0 1-1.265 1.265L7.76 16.24l1.804-5.411a2 2 0 0 1 1.265-1.265z"/><circle cx="12" cy="12" r="10"/>',
  help: '<circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><path d="M12 17h.01"/>',
  check: '<path d="M20 6 9 17l-5-5"/>',
  badge: '<path d="M3.85 8.62a4 4 0 0 1 4.78-4.77 4 4 0 0 1 6.74 0 4 4 0 0 1 4.78 4.78 4 4 0 0 1 0 6.74 4 4 0 0 1-4.77 4.78 4 4 0 0 1-6.75 0 4 4 0 0 1-4.78-4.77 4 4 0 0 1 0-6.76Z"/><path d="m9 12 2 2 4-4"/>',
  shield: '<path d="M20 13c0 5-3.5 7.5-7.66 8.95a1 1 0 0 1-.67-.01C7.5 20.5 4 18 4 13V6a1 1 0 0 1 1-1c2 0 4.5-1.2 6.24-2.72a1.17 1.17 0 0 1 1.52 0C14.51 3.81 17 5 19 5a1 1 0 0 1 1 1z"/><path d="m9 12 2 2 4-4"/>',
  doc: '<path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"/><path d="M14 2v4a2 2 0 0 0 2 2h4"/><path d="M10 9H8"/><path d="M16 13H8"/><path d="M16 17H8"/>',
  pencil: '<path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/>',
  plus: '<path d="M5 12h14"/><path d="M12 5v14"/>',
  tag: '<path d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z"/><circle cx="7.5" cy="7.5" r=".5" fill="currentColor"/>',
  camera: '<path d="M14.5 4h-5L7 7H4a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2h-3l-2.5-3z"/><circle cx="12" cy="13" r="3"/>',
  message: '<path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>',
  package: '<path d="M11 21.73a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73z"/><path d="M12 22V12"/><path d="m3.3 7 7.703 4.734a2 2 0 0 0 1.994 0L20.7 7"/><path d="m7.5 4.27 9 5.15"/>',
  send: '<path d="M14.536 21.686a.5.5 0 0 0 .937-.024l6.5-19a.496.496 0 0 0-.635-.635l-19 6.5a.5.5 0 0 0-.024.937l7.93 3.18a2 2 0 0 1 1.112 1.11z"/><path d="m21.854 2.147-10.94 10.939"/>',
  truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18H9"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
};

function icon(name) {
  var p = ICONS[name] || ICONS.compass;
  return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + p + '</svg>';
}

var CSS = [
  '.gwt-root{position:fixed;inset:0;z-index:' + Z + ';pointer-events:none;font-family:inherit;-webkit-font-smoothing:antialiased}',
  '.gwt-root *,.gwt-root *::before,.gwt-root *::after{box-sizing:border-box}',
  '.gwt-overlay{position:fixed;inset:0;width:100%;height:100%;pointer-events:none;opacity:0;transition:opacity .35s ease}',
  '.gwt-root.is-on .gwt-overlay{opacity:1}',
  '.gwt-blocker{position:fixed;inset:0;pointer-events:auto;background:transparent;cursor:default}',
  '.gwt-ring{position:fixed;pointer-events:none;border-radius:12px;opacity:0;transition:opacity .25s ease;box-shadow:0 0 0 2px rgba(255,255,255,.95),0 0 0 7px rgba(59,91,219,.28)}',
  '.gwt-ring.is-on{opacity:1}',
  '.gwt-ring::after{content:"";position:absolute;inset:-5px;border-radius:inherit;border:2px solid rgba(217,39,46,.6);animation:gwt-pulse 1.9s cubic-bezier(.2,.6,.3,1) infinite}',
  '.gwt-ring.is-try::after{border-color:rgba(217,39,46,.85);animation-duration:1.3s}',
  '@keyframes gwt-pulse{0%{transform:scale(1);opacity:.9}100%{transform:scale(1.06);opacity:0}}',
  '.gwt-card{position:fixed;left:0;top:0;pointer-events:auto;width:368px;max-width:calc(100vw - 24px);background:#fff;color:#0f172a;border-radius:16px;box-shadow:0 28px 64px -14px rgba(2,8,40,.5),0 2px 6px rgba(2,8,40,.14);opacity:0;transform:translateY(8px) scale(.975);transition:opacity .22s ease,transform .32s cubic-bezier(.2,.8,.2,1)}',
  '.gwt-card.is-in{opacity:1;transform:none}',
  '.gwt-card:focus{outline:none}',
  '.gwt-progress{position:absolute;top:0;left:18px;right:18px;height:3px;background:#eef1f8;border-radius:0 0 3px 3px;overflow:hidden}',
  '.gwt-progress>i{display:block;height:100%;width:0;background:#001689;transition:width .5s cubic-bezier(.2,.8,.2,1)}',
  '.gwt-inner{padding:16px 18px 16px}',
  '.gwt-head{display:flex;align-items:center;gap:10px;margin-bottom:12px}',
  '.gwt-icon{flex:0 0 34px;height:34px;border-radius:8px;display:grid;place-items:center;background:#001689;color:#fff}',
  '.gwt-icon svg{width:18px;height:18px}',
  '.gwt-eyebrow{font-size:11px;font-weight:700;letter-spacing:.09em;text-transform:uppercase;color:#6b7387}',
  '.gwt-eyebrow b{color:#001689}',
  '.gwt-x{margin-left:auto;width:30px;height:30px;border-radius:9px;border:0;background:transparent;color:#6b7387;cursor:pointer;display:grid;place-items:center;transition:background .15s,color .15s}',
  '.gwt-x:hover{background:#f1f3f9;color:#0f172a}',
  '.gwt-x svg{width:16px;height:16px}',
  '.gwt-root .gwt-title{font-family:inherit;text-transform:none;font-size:17px!important;line-height:1.3;font-weight:800;margin:0 0 6px!important;padding:0;color:#0b1435!important;letter-spacing:-.012em}',
  '.gwt-root .gwt-body{text-transform:none;letter-spacing:normal}',
  '.gwt-root .gwt-btn{white-space:nowrap;text-transform:none;letter-spacing:normal}',
  '.gwt-body{font-size:14px;line-height:1.58;color:#3d4659;margin:0}',
  '.gwt-body b,.gwt-body strong{color:#0b1435;font-weight:700}',
  '.gwt-body kbd{font:inherit;font-size:12px;padding:1px 6px;border-radius:5px;background:#f1f3f9;border:1px solid #dde2ee;color:#0b1435}',
  '.gwt-hint{margin-top:13px;display:flex;align-items:center;gap:10px;padding:10px 12px;border-radius:11px;background:#fff6f6;border:1px solid #ffd6d8;color:#a01d24;font-size:13px;font-weight:650;transition:background .3s,border-color .3s,color .3s}',
  '.gwt-dot{flex:0 0 8px;height:8px;border-radius:50%;background:#d9272e;animation:gwt-ping 1.4s infinite}',
  '@keyframes gwt-ping{0%{box-shadow:0 0 0 0 rgba(217,39,46,.55)}80%{box-shadow:0 0 0 8px rgba(217,39,46,0)}100%{box-shadow:0 0 0 0 rgba(217,39,46,0)}}',
  '.gwt-hint.is-done{background:#effaf3;border-color:#bfe8cd;color:#176b37}',
  '.gwt-hint.is-done .gwt-dot{background:#1f9d55;animation:none}',
  '.gwt-foot{display:flex;align-items:center;gap:8px;margin-top:16px}',
  '.gwt-dots{display:flex;gap:5px;margin-right:auto;align-items:center}',
  '.gwt-dots i{width:6px;height:6px;border-radius:99px;background:#d7dcea;transition:width .3s,background .3s}',
  '.gwt-dots i.on{width:18px;background:#001689}',
  '.gwt-dots i.done{background:#9fb0ea}',
  '.gwt-count{margin-right:auto;font-size:12px;color:#6b7387;font-weight:600}',
  '.gwt-btn{appearance:none;border:0;border-radius:10px;padding:9px 14px;font:inherit;font-size:13.5px;font-weight:700;line-height:1.2;cursor:pointer;transition:background .15s,transform .1s,box-shadow .15s,color .15s;display:inline-flex;align-items:center;gap:6px}',
  '.gwt-btn:active{transform:translateY(1px)}',
  '.gwt-btn svg{width:15px;height:15px}',
  '.gwt-btn-ghost{background:transparent;color:#3d4659}',
  '.gwt-btn-ghost:hover{background:#f1f3f9;color:#0b1435}',
  '.gwt-btn-primary{background:#001689;color:#fff;box-shadow:0 8px 16px -8px rgba(0,22,137,.7)}',
  '.gwt-btn-primary:hover{background:#0b25b0}',
  '.gwt-btn-primary.is-glow{animation:gwt-glow 1.6s ease-in-out infinite}',
  '@keyframes gwt-glow{0%,100%{box-shadow:0 8px 16px -8px rgba(0,22,137,.7)}50%{box-shadow:0 0 0 5px rgba(59,91,219,.22),0 8px 16px -8px rgba(0,22,137,.7)}}',
  '.gwt-btn:focus-visible,.gwt-x:focus-visible{outline:2px solid #3b5bdb;outline-offset:2px}',
  '.gwt-arrow{position:absolute;width:14px;height:14px;background:#fff;transform:rotate(45deg);border-radius:3px}',
  '.gwt-card[data-side=bottom] .gwt-arrow{top:-6px}',
  '.gwt-card[data-side=top] .gwt-arrow{bottom:-6px}',
  '.gwt-card[data-side=right] .gwt-arrow{left:-6px}',
  '.gwt-card[data-side=left] .gwt-arrow{right:-6px}',
  '.gwt-card[data-side=center] .gwt-arrow,.gwt-card.is-sheet .gwt-arrow{display:none}',
  '.gwt-card[data-side=center]{width:420px;text-align:left}',
  '.gwt-card.is-sheet{left:10px!important;right:10px!important;top:auto!important;bottom:calc(10px + env(safe-area-inset-bottom))!important;width:auto;max-width:none;border-radius:18px}',
  '.gwt-card.nudge{animation:gwt-nudge .42s cubic-bezier(.36,.07,.19,.97)}',
  '@keyframes gwt-nudge{0%,100%{translate:0}20%{translate:-6px 0}40%{translate:6px 0}60%{translate:-3px 0}80%{translate:3px 0}}',
  '.gwt-hero{display:grid;place-items:center;margin:4px 0 12px}',
  '.gwt-hero span{position:relative;width:56px;height:56px;border-radius:12px;display:grid;place-items:center;color:#fff;background:#001689;overflow:hidden}',
  '.gwt-hero span::after{content:"";position:absolute;right:-4px;top:-6px;width:12px;height:70px;background:#d9272e;transform:skewX(-24deg)}',
  '.gwt-hero span svg{position:relative;z-index:1;width:26px;height:26px}',
  '.gwt-hero.is-success span{background:#178a49;animation:gwt-pop .5s cubic-bezier(.2,1.4,.4,1)}',
  '.gwt-hero.is-success span::after{display:none}',
  '@keyframes gwt-pop{0%{transform:scale(.4);opacity:0}100%{transform:scale(1);opacity:1}}',
  '.gwt-card[data-side=center] .gwt-title,.gwt-card[data-side=center] .gwt-body{text-align:center}',
  '.gwt-sr{position:absolute!important;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}',
  '@media (prefers-reduced-motion:reduce){.gwt-root *,.gwt-root *::after,.gwt-root *::before{transition:none!important;animation:none!important}}',
].join('\n');

function injectStyles() {
  if (document.getElementById(STYLE_ID)) return;
  var s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = CSS;
  (document.head || document.documentElement).appendChild(s);
}

var reducedMotion = (function () {
  try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) { return false; }
})();

function esc(s) { var d = document.createElement('div'); d.textContent = s == null ? '' : String(s); return d.innerHTML; }
function vw() { return window.innerWidth || document.documentElement.clientWidth; }
function vh() { return window.innerHeight || document.documentElement.clientHeight; }
function sleep(ms) { return new Promise(function (r) { setTimeout(r, ms); }); }
function ease(t) { return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }

function isVisible(el) {
  if (!el || !el.isConnected) return false;
  var r = el.getBoundingClientRect();
  if (r.width < 2 || r.height < 2) return false;
  var cs = getComputedStyle(el);
  return cs.visibility !== 'hidden' && cs.display !== 'none' && parseFloat(cs.opacity || '1') > 0.05;
}

function resolveTarget(target) {
  if (!target) return null;
  try {
    if (typeof target === 'function') return target() || null;
    var list = document.querySelectorAll(target);
    for (var i = 0; i < list.length; i++) if (isVisible(list[i])) return list[i];
  } catch (e) { /* bad selector */ }
  return null;
}

/** Resolve once the target is rendered + visible, or null after `ms`. */
function waitForTarget(target, ms) {
  return new Promise(function (resolve) {
    var hit = resolveTarget(target);
    if (hit) return resolve(hit);
    var done = false;
    var mo = new MutationObserver(check);
    var iv = setInterval(check, 200);
    var to = setTimeout(function () { finish(null); }, ms);
    try { mo.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['class', 'style', 'hidden'] }); } catch (e) { /* ignore */ }
    function check() { var el = resolveTarget(target); if (el) finish(el); }
    function finish(el) {
      if (done) return; done = true;
      mo.disconnect(); clearInterval(iv); clearTimeout(to);
      resolve(el);
    }
  });
}

/** Bring the element into a comfortable viewing position and wait for scroll to settle. */
function scrollParent(el) {
  for (var n = el.parentElement; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
    var cs = getComputedStyle(n);
    if (/(auto|scroll|overlay)/.test(cs.overflowY) && n.scrollHeight > n.clientHeight + 1) return n;
  }
  return null;
}
function inFixedLayer(el) {
  for (var n = el; n && n !== document.body; n = n.parentElement) {
    if (getComputedStyle(n).position === 'fixed') return true;
  }
  return false;
}
async function settle(el) {
  var last = -1, stable = 0;
  for (var i = 0; i < 40 && stable < 3; i++) {
    await sleep(40);
    var y = el.getBoundingClientRect().top;
    if (Math.abs(y - last) < 0.5) stable++; else stable = 0;
    last = y;
  }
}
async function bringIntoView(el, sheet) {
  // Targets inside a scrolling panel (the Add-vehicle modal body, the store
  // drawer list) are scrolled within that panel first.
  var sp = scrollParent(el);
  if (sp) {
    var er = el.getBoundingClientRect(), cr = sp.getBoundingClientRect();
    var spBottomSafe = sheet ? Math.max(0, cr.bottom - (vh() - 300)) : 0;
    if (er.top < cr.top + 8 || er.bottom > cr.bottom - 8 - spBottomSafe) {
      var delta = er.top - cr.top - Math.max(12, (cr.height - spBottomSafe - er.height) / 2);
      sp.scrollTo({ top: Math.max(0, sp.scrollTop + delta), behavior: reducedMotion ? 'auto' : 'smooth' });
      await settle(el);
    }
  }
  // Fixed overlays (drawers, modals) don't move with the window.
  if (inFixedLayer(el)) return;
  var r = el.getBoundingClientRect();
  var topSafe = 84; // clear the sticky header
  var bottomSafe = sheet ? 300 : 40; // leave room for the bottom sheet on phones
  var fits = r.top >= topSafe && r.bottom <= vh() - bottomSafe;
  if (fits) return;
  var tallerThanView = r.height > vh() - topSafe - bottomSafe;
  var target = tallerThanView || sheet
    ? window.scrollY + r.top - topSafe
    : window.scrollY + r.top - (vh() - r.height) / 2;
  window.scrollTo({ top: Math.max(0, target), behavior: reducedMotion ? 'auto' : 'smooth' });
  // Wait until the element stops moving (smooth scroll / layout shifts).
  await settle(el);
}

// ── storage ────────────────────────────────────────────────────────────────
function key(id, version) { return 'gwt:' + id + ':v' + (version || 1); }
export function getTourState(id, version) {
  try { return localStorage.getItem(key(id, version)) || ''; } catch (e) { return ''; }
}
export function setTourState(id, version, state) {
  try { localStorage.setItem(key(id, version), state); } catch (e) { /* private mode */ }
}

function emit(action, tour, stepIndex) {
  var detail = { tour: tour.id, action: action, step: stepIndex };
  try { window.dispatchEvent(new CustomEvent('gw:tour', { detail: detail })); } catch (e) { /* ignore */ }
  try { if (window.dataLayer && window.dataLayer.push) window.dataLayer.push({ event: 'gw_tour', gw_tour_id: tour.id, gw_tour_action: action, gw_tour_step: stepIndex }); } catch (e) { /* ignore */ }
}

// ── the engine ────────────────────────────────────────────────────────────
var active = null;
export function isTourActive() { return !!active; }
export function endActiveTour(reason) { if (active) active.close(reason || 'skipped'); }

/**
 * Run a tour. Resolves with 'completed' | 'skipped' when it ends.
 */
export function runTour(tour, opts) {
  opts = opts || {};
  if (active) active.close('replaced');
  injectStyles();

  return new Promise(function (resolveRun) {
    var uid = 'gwt' + Math.random().toString(36).slice(2, 8);
    var steps = tour.steps.slice();
    var idx = -1;
    var current = null;      // { step, el }
    var hole = { x: vw() / 2, y: vh() / 2, w: 0, h: 0, r: 12 };
    var tweenId = 0;
    var raf = 0;
    var ro = null;
    var detachInteractive = null;
    var advanceTimer = 0;
    var closed = false;
    var lastFocus = document.activeElement;
    var navToken = 0;

    // DOM
    var root = document.createElement('div');
    root.className = 'gwt-root';
    root.setAttribute('data-tour-id', tour.id);
    root.innerHTML =
      '<svg class="gwt-overlay" aria-hidden="true"><defs><mask id="' + uid + '-m">' +
      '<rect width="100%" height="100%" fill="#fff"/><rect class="gwt-hole" fill="#000"/></mask></defs>' +
      '<rect width="100%" height="100%" fill="rgba(5,11,38,.62)" mask="url(#' + uid + '-m)"/></svg>' +
      '<div class="gwt-blocker"></div><div class="gwt-ring"></div>' +
      '<div class="gwt-card" role="dialog" aria-modal="true" tabindex="-1" aria-labelledby="' + uid + '-t" aria-describedby="' + uid + '-b"></div>' +
      '<div class="gwt-sr" aria-live="polite"></div>';
    document.body.appendChild(root);
    var svgHole = root.querySelector('.gwt-hole');
    var blocker = root.querySelector('.gwt-blocker');
    var ring = root.querySelector('.gwt-ring');
    var card = root.querySelector('.gwt-card');
    var live = root.querySelector('.gwt-sr');
    requestAnimationFrame(function () { root.classList.add('is-on'); });

    blocker.addEventListener('click', function () {
      card.classList.remove('nudge'); void card.offsetWidth; card.classList.add('nudge');
    });

    function isSheet() { return vw() < 640; }

    // ── geometry ──────────────────────────────────────────────────────────
    function unionRect(el, step) {
      var r = el.getBoundingClientRect();
      var box = { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
      // Grow to cover open dropdowns/popovers rendered inside the target (e.g. a
      // vehicle picker's option list is absolutely positioned below its button).
      // Only positioned layers count: their box is already clipped to max-height,
      // whereas individual option rows extend past the scroll viewport.
      if (step.includeDescendants) {
        var kids = el.querySelectorAll('*');
        for (var k = 0; k < kids.length && k < 600; k++) {
          var pos = getComputedStyle(kids[k]).position;
          if (pos !== 'absolute' && pos !== 'fixed') continue;
          var c = kids[k].getBoundingClientRect();
          if (c.width < 2 || c.height < 2) continue;
          box.left = Math.min(box.left, c.left); box.top = Math.min(box.top, c.top);
          box.right = Math.max(box.right, c.right); box.bottom = Math.max(box.bottom, c.bottom);
        }
      }
      return box;
    }
    function targetBox(el, step) {
      var pad = step.padding == null ? 8 : step.padding;
      var r = unionRect(el, step);
      var x = Math.max(4, r.left - pad), y = Math.max(4, r.top - pad);
      var x2 = Math.min(vw() - 4, r.right + pad), y2 = Math.min(vh() - 4, r.bottom + pad);
      return { x: x, y: y, w: Math.max(0, x2 - x), h: Math.max(0, y2 - y), r: step.radius == null ? 12 : step.radius };
    }
    function centreBox() { return { x: vw() / 2, y: vh() / 2, w: 0, h: 0, r: 12 }; }

    function paintHole(b, allowClicks) {
      hole = b;
      svgHole.setAttribute('x', b.x); svgHole.setAttribute('y', b.y);
      svgHole.setAttribute('width', b.w); svgHole.setAttribute('height', b.h);
      svgHole.setAttribute('rx', b.r); svgHole.setAttribute('ry', b.r);
      ring.style.left = b.x + 'px'; ring.style.top = b.y + 'px';
      ring.style.width = b.w + 'px'; ring.style.height = b.h + 'px';
      ring.style.borderRadius = b.r + 'px';
      if (allowClicks && b.w > 0) {
        var x1 = b.x, y1 = b.y, x2 = b.x + b.w, y2 = b.y + b.h;
        blocker.style.clipPath = 'polygon(evenodd,0 0,100% 0,100% 100%,0 100%,0 0,' +
          x1 + 'px ' + y1 + 'px,' + x2 + 'px ' + y1 + 'px,' + x2 + 'px ' + y2 + 'px,' + x1 + 'px ' + y2 + 'px,' + x1 + 'px ' + y1 + 'px)';
      } else {
        blocker.style.clipPath = 'none';
      }
    }

    function animateHole(to, allowClicks) {
      var from = { x: hole.x, y: hole.y, w: hole.w, h: hole.h, r: hole.r };
      var id = ++tweenId;
      if (reducedMotion) { paintHole(to, allowClicks); return Promise.resolve(); }
      return new Promise(function (res) {
        var t0 = performance.now(), dur = 440;
        (function frame(now) {
          if (id !== tweenId) return res();
          var k = ease(Math.min(1, (now - t0) / dur));
          paintHole({
            x: from.x + (to.x - from.x) * k, y: from.y + (to.y - from.y) * k,
            w: from.w + (to.w - from.w) * k, h: from.h + (to.h - from.h) * k,
            r: from.r + (to.r - from.r) * k,
          }, allowClicks);
          if (k < 1) requestAnimationFrame(frame); else res();
        })(t0);
      });
    }

    function placeCard() {
      if (!current) return;
      var step = current.step;
      var sheet = isSheet() && current.el;
      card.classList.toggle('is-sheet', !!sheet);
      card.style.removeProperty('right'); card.style.removeProperty('bottom');
      var arrow = card.querySelector('.gwt-arrow');
      if (!current.el) {
        card.setAttribute('data-side', 'center');
        var cw0 = card.offsetWidth, ch0 = card.offsetHeight;
        card.style.left = Math.round((vw() - cw0) / 2) + 'px';
        card.style.top = Math.round(Math.max(16, (vh() - ch0) / 2)) + 'px';
        return;
      }
      if (sheet) { card.setAttribute('data-side', 'bottom'); return; }

      var b = hole;
      var cw = card.offsetWidth, ch = card.offsetHeight;
      var space = { bottom: vh() - (b.y + b.h), top: b.y, right: vw() - (b.x + b.w), left: b.x };
      var need = { bottom: ch + GAP + 12, top: ch + GAP + 12, right: cw + GAP + 12, left: cw + GAP + 12 };
      var order = ['bottom', 'top', 'right', 'left'];
      if (step.placement && step.placement !== 'auto') order = [step.placement].concat(order.filter(function (s) { return s !== step.placement; }));
      var side = order.find(function (s) { return space[s] >= need[s]; }) || order.slice().sort(function (a, c) { return space[c] - space[a]; })[0];
      var left, top;
      var cx = b.x + b.w / 2, cy = b.y + b.h / 2;
      if (side === 'bottom' || side === 'top') {
        left = Math.min(Math.max(12, cx - cw / 2), vw() - cw - 12);
        top = side === 'bottom' ? b.y + b.h + GAP : b.y - GAP - ch;
        top = Math.min(Math.max(12, top), vh() - ch - 12);
        if (arrow) { arrow.style.left = Math.min(Math.max(18, cx - left - 7), cw - 32) + 'px'; arrow.style.top = ''; }
      } else {
        top = Math.min(Math.max(12, cy - ch / 2), vh() - ch - 12);
        left = side === 'right' ? b.x + b.w + GAP : b.x - GAP - cw;
        left = Math.min(Math.max(12, left), vw() - cw - 12);
        if (arrow) { arrow.style.top = Math.min(Math.max(16, cy - top - 7), ch - 30) + 'px'; arrow.style.left = ''; }
      }
      card.setAttribute('data-side', side);
      card.style.left = Math.round(left) + 'px';
      card.style.top = Math.round(top) + 'px';
    }

    function track() {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(function () {
        if (!current || closed) return;
        if (current.el) {
          if (!current.el.isConnected) {
            // The SPA re-rendered the node - re-resolve the same target.
            var again = resolveTarget(current.step.target);
            if (!again) return;
            current.el = again;
            observe(again);
          }
          tweenId++; // cancel any running tween; follow the element exactly
          paintHole(targetBox(current.el, current.step), !!current.step.interactive);
        } else {
          paintHole(centreBox(), false);
        }
        placeCard();
      });
    }
    var mo = null;
    function observe(el) {
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      if (window.ResizeObserver && el) { ro = new ResizeObserver(track); ro.observe(el); }
      // Dropdowns opening/closing inside the target don't resize it - watch the subtree.
      if (el) { mo = new MutationObserver(track); try { mo.observe(el, { childList: true, subtree: true, attributes: true }); } catch (e) { /* ignore */ } }
    }

    // ── rendering ─────────────────────────────────────────────────────────
    var shown = {}; // steps already displayed keep their place in the count
    // Steps whose `when()` is false right now are skipped, so the counter, dots
    // and progress bar count only the applicable ones (no "Step 3 → Step 5" gaps).
    function applicable() {
      var out = [];
      for (var a = 0; a < steps.length; a++) {
        var ok = true;
        try { if (steps[a].when && steps[a].when() === false) ok = false; } catch (e) { ok = false; }
        // `count: true` keeps a conditional step in the total even before it applies
        // (e.g. Add-vehicle form steps), so the counter doesn't grow mid-tour.
        var c = steps[a].count; try { c = typeof c === 'function' ? !!c() : !!c; } catch (e2) { c = false; }
        if (ok || a === idx || shown[a] || c) out.push(a);
      }
      return out;
    }
    function val(v) { try { return typeof v === 'function' ? v() : v; } catch (e) { return ''; } }
    function renderCard(step, rawIndex) {
      shown[rawIndex] = true;
      var list = applicable();
      var total = list.length;
      var i = Math.max(0, list.indexOf(rawIndex));
      var isLast = i === total - 1;
      var isCentre = !current.el;
      var dots = '';
      if (total <= 9) {
        for (var d = 0; d < total; d++) dots += '<i class="' + (d === i ? 'on' : d < i ? 'done' : '') + '"></i>';
        dots = '<span class="gwt-dots" aria-hidden="true">' + dots + '</span>';
      } else {
        dots = '<span class="gwt-dots" aria-hidden="true"></span>'; // the eyebrow + progress bar already show position
      }
      var hero = isCentre && step.hero
        ? '<div class="gwt-hero' + (step.hero === 'success' ? ' is-success' : '') + '"><span>' + icon(step.hero === 'success' ? 'check' : (step.icon || 'compass')) + '</span></div>'
        : '';
      var head = isCentre ? '' :
        '<div class="gwt-head"><span class="gwt-icon">' + icon(step.icon) + '</span>' +
        '<span class="gwt-eyebrow">Step <b>' + (i + 1) + '</b> of ' + total + '</span>' +
        '<button type="button" class="gwt-x" data-act="close" aria-label="Close tour"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>';
      var hint = step.interactive
        ? '<div class="gwt-hint" role="status"><span class="gwt-dot"></span><span class="gwt-hint-text">' + esc(step.interactive.hint || 'Give it a try') + '</span></div>'
        : '';
      var back = i > 0 && !step.hideBack ? '<button type="button" class="gwt-btn gwt-btn-ghost" data-act="back">Back</button>' : '';
      var nextLabel = step.nextLabel || (isLast ? 'Finish' : (i === 0 && isCentre ? 'Show me around' : 'Next'));
      var nextIcon = isLast
        ? '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5"/></svg>'
        : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';
      var skip = isCentre && i === 0 ? '<button type="button" class="gwt-btn gwt-btn-ghost" data-act="close">Not now</button>' : back;
      card.innerHTML =
        '<div class="gwt-progress"><i style="width:' + Math.round(((i + 1) / total) * 100) + '%"></i></div>' +
        '<div class="gwt-inner">' + head + hero +
        '<h3 class="gwt-title" id="' + uid + '-t">' + esc(val(step.title)) + '</h3>' +
        '<p class="gwt-body" id="' + uid + '-b">' + (val(step.body) || '') + '</p>' + hint +
        '<div class="gwt-foot">' + (isCentre ? '<span style="margin-right:auto"></span>' : dots) + skip +
        '<button type="button" class="gwt-btn gwt-btn-primary" data-act="next">' + esc(nextLabel) + nextIcon + '</button></div>' +
        '</div>' + (isCentre ? '' : '<span class="gwt-arrow" aria-hidden="true"></span>');
      live.textContent = 'Step ' + (i + 1) + ' of ' + total + ': ' + val(step.title);
    }

    card.addEventListener('click', function (e) {
      var b = e.target.closest && e.target.closest('[data-act]');
      if (!b) return;
      var act = b.getAttribute('data-act');
      if (act === 'next') go(idx + 1, 1);
      else if (act === 'back') go(idx - 1, -1);
      else if (act === 'close') close(idx >= steps.length - 1 ? 'completed' : 'skipped');
    });

    function onKey(e) {
      if (closed) return;
      if (e.key === 'Escape') { e.preventDefault(); close('skipped'); return; }
      var typing = e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName) && !card.contains(e.target);
      if (typing) return; // let the user type into the highlighted field
      if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1, 1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); if (idx > 0) go(idx - 1, -1); }
      else if (e.key === 'Tab' && card.contains(document.activeElement)) {
        var f = card.querySelectorAll('button');
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    }
    document.addEventListener('keydown', onKey, true);
    window.addEventListener('scroll', track, true);
    window.addEventListener('resize', track);

    function bindInteractive(step, el) {
      if (detachInteractive) { detachInteractive(); detachInteractive = null; }
      ring.classList.toggle('is-try', !!step.interactive);
      if (!step.interactive) return;
      var it = step.interactive;
      var host = (it.target && resolveTarget(it.target)) || el;
      if (!host) return;
      var evts = it.on ? it.on.split(/\s+/) : [];
      var fired = false;
      var poll = 0;
      function handler(evt) {
        if (fired || closed) return;
        if (it.validate && !it.validate(host, evt)) return;
        clearInterval(poll);
        fired = true;
        var h = card.querySelector('.gwt-hint');
        if (h) { h.classList.add('is-done'); var t = h.querySelector('.gwt-hint-text'); if (t) t.textContent = it.done || 'Nice, that\'s it!'; }
        var nb = card.querySelector('[data-act=next]');
        if (nb) nb.classList.add('is-glow');
        emit('interact', tour, idx);
        if (it.autoAdvance !== false) {
          var my = idx;
          clearTimeout(advanceTimer);
          advanceTimer = setTimeout(function () { if (!closed && idx === my) go(idx + 1, 1); }, it.delay || 1100);
        }
      }
      // Capture phase on the host's subtree so React-managed inputs still report.
      evts.forEach(function (ev) { host.addEventListener(ev, handler, true); });
      // React pickers emit no DOM change event - `check()` polls the UI state instead.
      if (typeof it.check === 'function') {
        poll = setInterval(function () { var ok = false; try { ok = !!it.check(); } catch (e) { /* ignore */ } if (ok) handler(null); }, 300);
      }
      detachInteractive = function () { clearInterval(poll); evts.forEach(function (ev) { host.removeEventListener(ev, handler, true); }); };
    }

    // ── navigation ────────────────────────────────────────────────────────
    async function go(next, dir) {
      if (closed) return;
      clearTimeout(advanceTimer);
      if (next >= steps.length) { close('completed'); return; }
      if (next < 0) return;
      var token = ++navToken;

      // Resolve the next showable step (skipping ones that don't apply).
      var i = next, step, el = null;
      while (i >= 0 && i < steps.length) {
        step = steps[i];
        var ok = true;
        try { if (step.when && step.when() === false) ok = false; } catch (e) { ok = false; }
        if (ok) {
          if (step.before) { try { await step.before(); } catch (e) { /* keep going */ } }
          if (token !== navToken || closed) return;
          if (!step.target) { el = null; break; }
          el = await waitForTarget(step.target, step.waitFor == null ? 6000 : step.waitFor);
          if (token !== navToken || closed) return;
          if (el) break;
          if (!step.optional) { el = null; break; } // show it centred rather than lose the message
        }
        i += dir || 1;
      }
      if (i < 0) return;
      if (i >= steps.length) { close('completed'); return; }

      // Out with the old card.
      card.classList.remove('is-in');
      if (!reducedMotion) await sleep(140);
      if (token !== navToken || closed) return;

      idx = i;
      current = { step: step, el: el };
      if (el) await bringIntoView(el, isSheet());
      if (token !== navToken || closed) return;
      observe(el);
      renderCard(step, i);
      placeCard();
      var allow = !!(el && step.interactive);
      ring.classList.toggle('is-on', !!el);
      await animateHole(el ? targetBox(el, step) : centreBox(), allow);
      if (token !== navToken || closed) return;
      placeCard();
      bindInteractive(step, el);
      card.classList.add('is-in');
      emit('step', tour, i);
      var nextBtn = card.querySelector('[data-act=next]');
      try { (nextBtn || card).focus({ preventScroll: true }); } catch (e) { /* ignore */ }
    }

    function close(reason) {
      if (closed) return;
      closed = true;
      navToken++;
      clearTimeout(advanceTimer);
      if (detachInteractive) detachInteractive();
      if (ro) ro.disconnect();
      if (mo) mo.disconnect();
      document.removeEventListener('keydown', onKey, true);
      window.removeEventListener('scroll', track, true);
      window.removeEventListener('resize', track);
      card.classList.remove('is-in');
      root.classList.remove('is-on');
      ring.classList.remove('is-on');
      setTimeout(function () { root.remove(); }, reducedMotion ? 0 : 360);
      active = null;
      setTourState(tour.id, tour.version, reason === 'completed' ? 'done' : 'skipped');
      emit(reason === 'completed' ? 'complete' : 'skip', tour, idx);
      try { if (lastFocus && lastFocus.focus) lastFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      if (typeof opts.onEnd === 'function') { try { opts.onEnd(reason); } catch (e) { /* ignore */ } }
      resolveRun(reason);
    }

    active = { close: close, tour: tour };
    emit('start', tour, 0);
    paintHole(centreBox(), false);
    go(opts.startAt || 0, 1);
  });
}
